import React from 'react';
import { useScrollAnimation, useScrollStagger, useParallaxEffect } from '../hooks/useScrollAnimation';
import './KiraLanding.css';

const KiraLanding: React.FC = () => {
  const [heroRef, heroIn] = useScrollAnimation({ threshold: 0.15 });
  const [featuresRef, featuresIn] = useScrollAnimation({ threshold: 0.2 });
  const { setRef: setFeatureRef, visibleElements: featureVisible } = useScrollStagger(3, { threshold: 0.15 });
  const { setRef: setUsecaseRef, visibleElements: usecaseVisible } = useScrollStagger(5, { threshold: 0.1 });
  const glowARef = useParallaxEffect(0.12);
  const glowBRef = useParallaxEffect(0.25);

  return (
    <div className="kira-page">
      {/* Hero */}
      <section className="kira-hero">
        <div className="bg-grid" aria-hidden>
          <div className="grid-overlay"></div>
          <div ref={glowARef as any} className="glow glow-a"></div>
          <div ref={glowBRef as any} className="glow glow-b"></div>
        </div>

        <div ref={heroRef} className={`container hero-inner reveal ${heroIn ? 'in' : ''}`}>
          <span className="badge">All‑in‑one Agent‑Native Fintech Infrastructure</span>
          <h1 className="headline gradient-animated">Embedded AI Fintech Products</h1>
          <p className="subhead">The first all‑in‑one infrastructure to launch embedded fintech products, supercharged by vertical AI agents and stablecoins.</p>

          <div className="cta-row">
            <a className="kira-btn primary" href="#contact">Talk to our founders</a>
            <a className="kira-btn ghost" href="#features">Explore the platform</a>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="kira-section">
        <div className="container">
          <div ref={featuresRef} className={`section-header reveal ${featuresIn ? 'in' : ''}`}>
            <h2 className="section-title">All‑in‑one Agent‑Native Fintech Infrastructure</h2>
            <p className="section-desc">Equip autonomous AI agents to seamlessly manage cross‑border transactions, compliance (KYC/KYB), fraud prevention, and treasury operations — all integrated into one programmable platform.</p>
          </div>

          <div className="feature-grid">
            <article ref={setFeatureRef(0)} className={`feature-card card-reveal ${featureVisible[0] ? 'in' : ''}`}>
              <h3>Universal Payment Gateway</h3>
              <p>Enable AI‑driven payment acceptance for cash, debit, ACH, and SWIFT via an agent‑friendly payment link. Perfect for conversational and autonomous flows.</p>
            </article>
            <article ref={setFeatureRef(1)} className={`feature-card card-reveal ${featureVisible[1] ? 'in' : ''}`}>
              <h3>AI‑Powered Treasury & Wallets</h3>
              <p>Automated treasury management with yield‑bearing wallets backed by stablecoins and U.S. Treasuries, optimized for cross‑border liquidity.</p>
            </article>
            <article ref={setFeatureRef(2)} className={`feature-card card-reveal ${featureVisible[2] ? 'in' : ''}`}>
              <h3>Agentic Compliance</h3>
              <p>Integrated KYC/KYB, AML, VASP and sanctions screening with programmable policies — reducing manual review without compromising standards.</p>
            </article>
          </div>
        </div>
      </section>

      {/* Use cases carousel (simplified as a stacked list with subtle motion) */}
      <section className="kira-section alt">
        <div className="container">
          <div className="usecases">
            <div ref={setUsecaseRef(0)} className={`usecase slide-up ${usecaseVisible[0] ? 'in' : ''}`}>
              <h3>Agentic Remittances</h3>
              <p>Launch agent‑friendly payment links to request and settle cross‑border payments with real‑time FX and built‑in compliance.</p>
            </div>
            <div ref={setUsecaseRef(1)} className={`usecase slide-up ${usecaseVisible[1] ? 'in' : ''}`}>
              <h3>Autonomous Treasury Operations</h3>
              <p>Policy‑driven liquidity routing, automated cash flow, and programmable limits with real‑time account visibility.</p>
            </div>
            <div ref={setUsecaseRef(2)} className={`usecase slide-up ${usecaseVisible[2] ? 'in' : ''}`}>
              <h3>Import/Export Trade Payments</h3>
              <p>Programmatic supplier settlements with compliance tracking, FX conversion, and status notifications.</p>
            </div>
            <div ref={setUsecaseRef(3)} className={`usecase slide-up ${usecaseVisible[3] ? 'in' : ''}`}>
              <h3>Programmable FX & Trading</h3>
              <p>On‑demand currency conversion APIs with transparent rates and auditability for agent‑to‑agent value exchange.</p>
            </div>
            <div ref={setUsecaseRef(4)} className={`usecase slide-up ${usecaseVisible[4] ? 'in' : ''}`}>
              <h3>Scalable Freelancer Payouts</h3>
              <p>Onboard, verify, and pay contractors globally via local rails or wallets — with tax‑compliant reporting.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Stablecoin benefits */}
      <section className="kira-section">
        <div className="container benefit">
          <div className="benefit-card">
            <h3>Stablecoin benefits, without the complexity</h3>
            <p>Instant global settlement, reduced FX costs, and yield opportunities — abstracted behind AI‑managed operations.</p>
          </div>
          <div className="benefit-card">
            <h3>AI‑managed instant global payouts</h3>
            <p>Trigger payouts in seconds to 35+ countries using localized rails with embedded compliance and programmable FX.</p>
          </div>
          <div className="benefit-card">
            <h3>No‑code or API</h3>
            <p>Deploy through intuitive dashboards or APIs designed for automated financial workflows.</p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="contact" className="kira-cta">
        <div className="container cta-inner glass">
          <h2>Build agent‑native fintech, fast</h2>
          <p>Talk to our founders to get early access and integrations.</p>
          <a className="kira-btn primary" href="mailto:info@kirafin.ai">Get in touch</a>
        </div>
      </section>
    </div>
  );
};

export default KiraLanding;


